// Centralized validation schemas for all asset types
import * as Yup from "yup";

// Import validation schemas from individual form files
import { insuranceValidationSchema } from "../components/asset-forms/insurance-form";
import { vehicleValidationSchema } from "../components/asset-forms/vehicle-form";
import { cashValidationSchema } from "../components/asset-forms/cash-form";
import { propertyValidationSchema } from "../components/asset-forms/property-form";
import { bankAccountsValidationSchema } from "../components/asset-forms/bank-accounts-form";
import { mutualFundsValidationSchema } from "../components/asset-forms/mutual-funds-form";
import { fixedDepositValidationSchema } from "../components/asset-forms/fixed-deposit-form";
import { stocksValidationSchema } from "../components/asset-forms/stocks-form";
import { cryptoValidationSchema } from "../components/asset-forms/crypto-form";
import { collectiblesValidationSchema } from "../components/asset-forms/collectibles-form";
import { businessOwnershipValidationSchema } from "../components/asset-forms/business-ownership-form";
import { otherPhysicalAssetsValidationSchema } from "../components/asset-forms/other-physical-assets-form";
import { importantPapersValidationSchema } from "../components/asset-forms/important-papers-form";
import { preciousMetalsValidationSchema } from "../components/asset-forms/precious-metals-form";

// Base validation schema for common fields
const baseAssetValidationSchema = Yup.object().shape({
  assetType: Yup.string().required("This field is required"),
  subCategory: Yup.string().required("This field is required"),
  notes: Yup.string()
    .max(500, "Notes must be less than 500 characters")
    .test(
      "safe-notes",
      "Notes contain invalid or unsafe content.",
      (value) => {
        if (!value) return true;
        const scriptPattern = /<script|<\/script|<iframe|<img|onerror=|onload=|javascript:|eval\(|alert\(|document\.|window\.|SELECT\s+|INSERT\s+|UPDATE\s+|DELETE\s+|DROP\s+|--|;|\*|\bOR\b|\bAND\b/i;
        const printablePattern = /^[\x20-\x7E\n\r\t]*$/;
        return !scriptPattern.test(value) && printablePattern.test(value);
      }
    ),
  isActive: Yup.string().required("Please select if this is active"),
});

// Registry of all validation schemas by subcategory
export const VALIDATION_SCHEMAS = {
  insurance: insuranceValidationSchema,
  vehicle: vehicleValidationSchema,
  cash: cashValidationSchema,
  property: propertyValidationSchema,
  "bank accounts": bankAccountsValidationSchema,
  "bank-accounts": bankAccountsValidationSchema, // Also support kebab-case for AssetFormWrapper
  "mutual funds": mutualFundsValidationSchema,
  "mutual-funds": mutualFundsValidationSchema, // Also support kebab-case for AssetFormWrapper
  "fixed deposit": fixedDepositValidationSchema,
  "fixed-deposit": fixedDepositValidationSchema, // Also support kebab-case for AssetFormWrapper
  stocks: stocksValidationSchema,
  crypto: cryptoValidationSchema,
  collectibles: collectiblesValidationSchema,
  "business ownership": businessOwnershipValidationSchema,
  "business-ownership": businessOwnershipValidationSchema, // Also support kebab-case for AssetFormWrapper
  "other physical assets": otherPhysicalAssetsValidationSchema,
  "other-physical-assets": otherPhysicalAssetsValidationSchema, // Also support kebab-case for AssetFormWrapper
  "important papers": importantPapersValidationSchema,
  "important-papers": importantPapersValidationSchema, // Also support kebab-case for AssetFormWrapper
  "precious metals": preciousMetalsValidationSchema,
  "precious-metals": preciousMetalsValidationSchema, // Also support kebab-case for AssetFormWrapper
};

/**
 * Get validation schema for a specific asset subcategory
 * Merges base schema with subcategory-specific schema
 * @param {string} subCategory - Asset subcategory
 * @returns {Yup.ObjectSchema} Merged validation schema
 */
export const getValidationSchema = (subCategory) => {
  const subSchema = VALIDATION_SCHEMAS[subCategory];

  // List of subcategories that don't need isActive validation
  const skipIsActiveFor = ["bank-accounts", "bank accounts"];

  // Create a modified base schema if isActive should be skipped
  let finalBaseSchema = baseAssetValidationSchema;
  if (skipIsActiveFor.includes(subCategory)) {
    finalBaseSchema = Yup.object().shape({
      assetType: Yup.string().required("This field is required"),
      subCategory: Yup.string().required("This field is required"),
      notes: Yup.string()
        .max(500, "Notes must be less than 500 characters")
        .test(
          "safe-notes",
          "Notes contain invalid or unsafe content.",
          (value) => {
            if (!value) return true;
            const scriptPattern = /<script|<\/script|<iframe|<img|onerror=|onload=|javascript:|eval\(|alert\(|document\.|window\.|SELECT\s+|INSERT\s+|UPDATE\s+|DELETE\s+|DROP\s+|--|;|\*|\bOR\b|\bAND\b/i;
            const printablePattern = /^[\x20-\x7E\n\r\t]*$/;
            return !scriptPattern.test(value) && printablePattern.test(value);
          }
        ),
      // isActive validation removed for these subcategories
    });
  }

  if (subSchema) {
    // Merge base schema with subcategory schema
    return finalBaseSchema.concat(subSchema);
  }

  // Fallback to base schema only
  return finalBaseSchema;
};

/**
 * Get validation schema for a specific asset subcategory (without base schema)
 * Used when you only need the subcategory-specific validation
 * @param {string} subCategory - Asset subcategory
 * @returns {Yup.ObjectSchema} Subcategory validation schema
 */
export const getSubCategoryValidationSchema = (subCategory) => {
  return VALIDATION_SCHEMAS[subCategory] || Yup.object();
};
